/**
 * Integration form types for the wizard and detail page.
 *
 * These are UI-friendly representations that mirror the canonical
 * IntegrationManifest from packages/sdks/common/node/src/integration-schema.ts.
 * Kept self-contained to avoid cross-package import issues.
 */

// ── Canonical types (mirrored from integration-schema.ts) ───────────

export type AuthType =
    | 'oauth2_authorization_code'
    | 'oauth2_client_credentials'
    | 'api_key'
    | 'basic_auth'
    | 'bearer_token'
    | 'hmac'
    | 'aws_sigv4'
    | 'custom_header';

export interface TokenInjection {
    location: 'header' | 'query' | 'body';
    key: string;
    template: string;
}

export interface OAuth2Strategy {
    auth_url: string;
    token_url: string;
    refresh_url?: string;
    scopes?: string[];
    pkce?: boolean;
    token_injection: TokenInjection;
}

export interface ApiKeyStrategy {
    required_fields: string[];
    validation_endpoint?: { method: string; path: string };
    token_injection: TokenInjection;
}

export interface BasicAuthStrategy {
    required_fields: ['username', 'password'];
    token_injection: TokenInjection;
}

export type AuthStrategyConfig =
    | OAuth2Strategy
    | ApiKeyStrategy
    | BasicAuthStrategy
    | Record<string, unknown>;

export interface IntegrationAppInfo {
    name: string;
    icon_url?: string;
    category?: string;
    description?: string;
    documentation_url?: string;
    vendor?: string;
}

export interface IntegrationAuth {
    supported_types: AuthType[];
    strategies: Partial<Record<AuthType, AuthStrategyConfig>>;
}

export interface RestMethodEntry {
    verb: string;
    path: string;
    description?: string;
    input_schema?: Record<string, unknown>;
    output_schema?: Record<string, unknown>;
    input_schema_ref?: string;
    output_schema_ref?: string;
    path_params?: string[];
    query_params?: string[];
}

export interface McpServerInfo {
    name?: string;
    version?: string;
}

export interface McpDiscoveredAuth {
    required: boolean;
    method: string;
    scheme?: string;
    www_authenticate?: string;
    resource_metadata_url?: string;
    issuer?: string;
    authorization_servers?: string[];
    authorization_endpoint?: string;
    token_endpoint?: string;
    refresh_endpoint?: string;
    revocation_endpoint?: string;
    introspection_endpoint?: string;
    registration_endpoint?: string;
    scopes_supported?: string[];
    grant_types_supported?: string[];
    code_challenge_methods_supported?: string[];
    resource?: string;
}

export interface McpConfig {
    transport: 'streamable_http';
    server_url: string;
    discovery: boolean;
    features?: string[];
    server_info?: McpServerInfo;
    discovered_auth?: McpDiscoveredAuth;
    tools?: { name?: string; description?: string }[];
    resources?: { name?: string; uri?: string; description?: string }[];
    prompts?: { name?: string; description?: string }[];
}

export interface IntegrationManifest {
    protocol: 'rest' | 'mcp';
    app: IntegrationAppInfo;
    auth: IntegrationAuth;
    base_url?: string;
    common_headers?: Record<string, string>;
    method_map?: Record<string, RestMethodEntry>;
    mcp?: McpConfig;
}

// ── Key-Value Pair ──────────────────────────────────────────────────

export interface KeyValuePair {
    key: string;
    value: string;
}

// ── Parameter Mapping ───────────────────────────────────────────────

export interface ParamMapping {
    key: string;
    valueExpression: string;
}

// ── App Info ────────────────────────────────────────────────────────

export interface AppInfoFormData {
    name: string;
    description: string;
    vendor: string;
    iconUrl: string;
    category: string;
    tags: string[];
    protocol: 'rest' | 'mcp';
    baseUrl: string;
    documentationUrl: string;
    commonHeaders: KeyValuePair[];
}

// ── Auth ────────────────────────────────────────────────────────────

export type WizardAuthType = 'none' | 'api_key' | 'basic_auth' | 'bearer_token' | 'oauth2';
export type ApiKeyDelivery = 'default' | 'bearer_token' | 'query' | 'header';
export type OAuth2GrantType = 'authorization_code' | 'client_credentials';
export type OAuth2AuthMethod = 'basic_header' | 'body';

export interface AuthFormData {
    authType: WizardAuthType;
    enabledAuthTypes: WizardAuthType[];
    apiKeyDelivery: ApiKeyDelivery;
    apiKeyParamName: string;
    oauth2GrantType: OAuth2GrantType;
    callbackUrl: string;
    authorizationUrl: string;
    refreshTokenUrl: string;
    accessTokenUrl: string;
    scopes: string;
    audience: string;
    oauth2AuthMethod: OAuth2AuthMethod;
    customHeaders: KeyValuePair[];
}

// ── Method ──────────────────────────────────────────────────────────

export type HttpVerb = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface MethodFormData {
    name: string;
    description: string;
    verb: HttpVerb;
    path: string;
    semantics: 'query' | 'work';
    channels: string[];
    pathParams: string[];
    queryParams: ParamMapping[];
    bodyParams: ParamMapping[];
    inputSchema: string;
    outputSchema: string;
}

// ── Top-level form state ────────────────────────────────────────────

export interface IntegrationFormData {
    appInfo: AppInfoFormData;
    auth: AuthFormData;
    methods: MethodFormData[];
}

// ── Defaults ────────────────────────────────────────────────────────

export function createDefaultFormData(): IntegrationFormData {
    return {
        appInfo: {
            name: '',
            description: '',
            vendor: '',
            iconUrl: '',
            category: '',
            tags: [],
            protocol: 'rest',
            baseUrl: '',
            documentationUrl: '',
            commonHeaders: [{ key: 'Content-Type', value: 'application/json' }],
        },
        auth: {
            authType: 'none',
            enabledAuthTypes: [],
            apiKeyDelivery: 'default',
            apiKeyParamName: '',
            oauth2GrantType: 'authorization_code',
            callbackUrl: '',
            authorizationUrl: '',
            refreshTokenUrl: '',
            accessTokenUrl: '',
            scopes: '',
            audience: '',
            oauth2AuthMethod: 'basic_header',
            customHeaders: [],
        },
        methods: [],
    };
}

export function createDefaultMethod(): MethodFormData {
    return {
        name: '',
        description: '',
        verb: 'GET',
        path: '',
        semantics: 'query',
        channels: ['sync'],
        pathParams: [],
        queryParams: [],
        bodyParams: [],
        inputSchema: '{\n  "type": "object",\n  "properties": {}\n}',
        outputSchema: '{\n  "type": "object"\n}',
    };
}

// ── Extract path params from {{placeholder}} patterns ───────────────

export function extractPathParams(path: string): string[] {
    const matches = path.match(/\{\{(\w+)\}\}/g);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
}

// ── Converters ──────────────────────────────────────────────────────

function buildTokenInjection(auth: AuthFormData): TokenInjection {
    if (auth.authType === 'api_key') {
        switch (auth.apiKeyDelivery) {
            case 'query':
                return { location: 'query', key: auth.apiKeyParamName || 'api_key', template: '{{api_key}}' };
            case 'header':
                return { location: 'header', key: auth.apiKeyParamName || 'X-API-Key', template: '{{api_key}}' };
            case 'bearer_token':
            case 'default':
            default:
                return { location: 'header', key: 'Authorization', template: 'Bearer {{api_key}}' };
        }
    }
    if (auth.authType === 'basic_auth') {
        return { location: 'header', key: 'Authorization', template: 'Basic {{base64(username:password)}}' };
    }
    if (auth.authType === 'bearer_token') {
        return { location: 'header', key: 'Authorization', template: 'Bearer {{access_token}}' };
    }
    return { location: 'header', key: 'Authorization', template: 'Bearer {{access_token}}' };
}

function buildAuthManifest(auth: AuthFormData): IntegrationAuth {
    const enabled = auth.enabledAuthTypes ?? [];
    if (enabled.length === 0) {
        return { supported_types: [], strategies: {} };
    }

    const supported_types: AuthType[] = [];
    const strategies: Partial<Record<AuthType, AuthStrategyConfig>> = {};

    for (const type of enabled) {
        if (type === 'none') continue;

        if (type === 'api_key') {
            supported_types.push('api_key');
            strategies.api_key = {
                required_fields: ['api_key'],
                token_injection: buildTokenInjection({ ...auth, authType: 'api_key' }),
            } satisfies ApiKeyStrategy;
        } else if (type === 'basic_auth') {
            supported_types.push('basic_auth');
            strategies.basic_auth = {
                required_fields: ['username', 'password'],
                token_injection: buildTokenInjection({ ...auth, authType: 'basic_auth' }),
            } satisfies BasicAuthStrategy;
        } else if (type === 'bearer_token') {
            supported_types.push('bearer_token');
            strategies.bearer_token = {
                required_fields: ['access_token'],
                token_injection: buildTokenInjection({ ...auth, authType: 'bearer_token' }),
            } satisfies ApiKeyStrategy;
        } else if (type === 'oauth2') {
            const authTypeKey: AuthType = auth.oauth2GrantType === 'client_credentials'
                ? 'oauth2_client_credentials'
                : 'oauth2_authorization_code';
            supported_types.push(authTypeKey);
            strategies[authTypeKey] = {
                auth_url: auth.authorizationUrl,
                token_url: auth.accessTokenUrl,
                refresh_url: auth.refreshTokenUrl || undefined,
                scopes: auth.scopes ? auth.scopes.split(',').map(s => s.trim()).filter(Boolean) : undefined,
                pkce: false,
                token_injection: { location: 'header', key: 'Authorization', template: 'Bearer {{access_token}}' },
            } satisfies OAuth2Strategy;
        }
    }

    return { supported_types, strategies };
}

function headersToRecord(headers: KeyValuePair[]): Record<string, string> {
    const record: Record<string, string> = {};
    for (const h of headers) {
        if (h.key.trim()) record[h.key.trim()] = h.value;
    }
    return record;
}

function recordToHeaders(record: Record<string, string> | undefined): KeyValuePair[] {
    if (!record) return [];
    return Object.entries(record).map(([key, value]) => ({ key, value }));
}

export function toIntegrationManifest(data: IntegrationFormData): {
    manifest: IntegrationManifest;
    tags: string[];
    description: string;
} {
    const methodMap: Record<string, RestMethodEntry> = {};
    for (const m of data.methods) {
        let parsedInput: Record<string, unknown> | undefined;
        let parsedOutput: Record<string, unknown> | undefined;
        try { parsedInput = JSON.parse(m.inputSchema); } catch { /* leave undefined */ }
        try { parsedOutput = JSON.parse(m.outputSchema); } catch { /* leave undefined */ }

        const entry: RestMethodEntry = {
            verb: m.verb,
            path: m.path,
            description: m.description || undefined,
            path_params: m.pathParams.length > 0 ? m.pathParams : undefined,
            query_params: m.queryParams.length > 0 ? m.queryParams.map(q => q.key) : undefined,
        };

        if (parsedInput && typeof parsedInput.$ref === 'string') {
            entry.input_schema_ref = parsedInput.$ref;
        } else {
            entry.input_schema = parsedInput;
        }
        if (parsedOutput && typeof parsedOutput.$ref === 'string') {
            entry.output_schema_ref = parsedOutput.$ref;
        } else {
            entry.output_schema = parsedOutput;
        }

        methodMap[m.name] = entry;
    }

    const commonHeaders = headersToRecord(data.appInfo.commonHeaders);
    const authHeaders = headersToRecord(data.auth.customHeaders);
    const allHeaders = { ...commonHeaders, ...authHeaders };

    const appInfo: IntegrationAppInfo = {
        name: data.appInfo.name,
        description: data.appInfo.description || undefined,
        icon_url: data.appInfo.iconUrl || undefined,
        category: data.appInfo.category || undefined,
        documentation_url: data.appInfo.documentationUrl || undefined,
        vendor: data.appInfo.vendor || undefined,
    };

    const manifest: IntegrationManifest = {
        protocol: data.appInfo.protocol,
        app: appInfo,
        auth: buildAuthManifest(data.auth),
        base_url: data.appInfo.baseUrl || undefined,
        common_headers: Object.keys(allHeaders).length > 0 ? allHeaders : undefined,
        method_map: Object.keys(methodMap).length > 0 ? methodMap : undefined,
    };

    if (data.appInfo.protocol === 'mcp' && data.appInfo.baseUrl) {
        manifest.mcp = {
            transport: 'streamable_http',
            server_url: data.appInfo.baseUrl,
            discovery: true,
        };
    }

    return {
        manifest,
        tags: data.appInfo.tags,
        description: data.appInfo.description,
    };
}

function inferWizardAuthType(auth: IntegrationAuth | undefined): WizardAuthType {
    if (!auth?.supported_types?.length) return 'none';
    const first = auth.supported_types[0];
    if (first === 'oauth2_authorization_code' || first === 'oauth2_client_credentials') return 'oauth2';
    if (first === 'api_key') return 'api_key';
    if (first === 'basic_auth') return 'basic_auth';
    if (first === 'bearer_token') return 'bearer_token';
    return 'none';
}

export function fromIntegrationManifest(
    manifest: Record<string, unknown>,
    tags?: string[],
): IntegrationFormData {
    const tc = manifest as Partial<IntegrationManifest> & { app?: IntegrationAppInfo & { vendor?: string } };
    const protocol: 'rest' | 'mcp' = tc.protocol === 'mcp' ? 'mcp' : 'rest';
    const mcpServerUrl = tc.mcp?.server_url;
    const auth = tc.auth as IntegrationAuth | undefined;
    const wizardAuth = inferWizardAuthType(auth);

    const enabledAuthTypes: WizardAuthType[] = [];
    if (auth?.supported_types) {
        for (const t of auth.supported_types) {
            if (t === 'api_key' && !enabledAuthTypes.includes('api_key')) enabledAuthTypes.push('api_key');
            else if (t === 'basic_auth' && !enabledAuthTypes.includes('basic_auth')) enabledAuthTypes.push('basic_auth');
            else if (t === 'bearer_token' && !enabledAuthTypes.includes('bearer_token')) enabledAuthTypes.push('bearer_token');
            else if ((t === 'oauth2_authorization_code' || t === 'oauth2_client_credentials') && !enabledAuthTypes.includes('oauth2')) enabledAuthTypes.push('oauth2');
        }
    }

    let oauth2GrantType: OAuth2GrantType = 'authorization_code';
    let authorizationUrl = '';
    let accessTokenUrl = '';
    let refreshTokenUrl = '';
    let scopes = '';

    if (wizardAuth === 'oauth2' && auth?.strategies) {
        const oauthKey = auth.supported_types?.find(t =>
            t === 'oauth2_authorization_code' || t === 'oauth2_client_credentials'
        );
        if (oauthKey === 'oauth2_client_credentials') {
            oauth2GrantType = 'client_credentials';
        }
        const strategy = auth.strategies[oauthKey!] as OAuth2Strategy | undefined;
        if (strategy) {
            authorizationUrl = strategy.auth_url ?? '';
            accessTokenUrl = strategy.token_url ?? '';
            refreshTokenUrl = strategy.refresh_url ?? '';
            scopes = strategy.scopes?.join(', ') ?? '';
        }
    }

    let apiKeyDelivery: ApiKeyDelivery = 'default';
    let apiKeyParamName = '';
    if (wizardAuth === 'api_key' && auth?.strategies?.api_key) {
        const strat = auth.strategies.api_key as ApiKeyStrategy;
        if (strat.token_injection?.location === 'query') {
            apiKeyDelivery = 'query';
            apiKeyParamName = strat.token_injection.key;
        } else if (strat.token_injection?.location === 'header' && strat.token_injection.key !== 'Authorization') {
            apiKeyDelivery = 'header';
            apiKeyParamName = strat.token_injection.key;
        } else if (strat.token_injection?.template?.includes('Bearer')) {
            apiKeyDelivery = 'bearer_token';
        }
    }

    const methods: MethodFormData[] = [];
    const methodMap = tc.method_map as Record<string, RestMethodEntry> | undefined;
    if (methodMap) {
        for (const [name, entry] of Object.entries(methodMap)) {
            let inputSchema: string;
            if (entry.input_schema_ref) {
                inputSchema = JSON.stringify({ $ref: entry.input_schema_ref }, null, 2);
            } else if (entry.input_schema) {
                inputSchema = JSON.stringify(entry.input_schema, null, 2);
            } else {
                inputSchema = '{\n  "type": "object",\n  "properties": {}\n}';
            }

            let outputSchema: string;
            if (entry.output_schema_ref) {
                outputSchema = JSON.stringify({ $ref: entry.output_schema_ref }, null, 2);
            } else if (entry.output_schema) {
                outputSchema = JSON.stringify(entry.output_schema, null, 2);
            } else {
                outputSchema = '{\n  "type": "object"\n}';
            }

            methods.push({
                name,
                description: entry.description ?? '',
                verb: (entry.verb?.toUpperCase() as HttpVerb) ?? 'GET',
                path: entry.path ?? '',
                semantics: 'query',
                channels: ['sync'],
                pathParams: entry.path_params ?? extractPathParams(entry.path ?? ''),
                queryParams: (entry.query_params ?? []).map(k => ({ key: k, valueExpression: `$.input.${k}` })),
                bodyParams: [],
                inputSchema,
                outputSchema,
            });
        }
    }

    return {
        appInfo: {
            name: tc.app?.name ?? '',
            description: tc.app?.description ?? '',
            vendor: tc.app?.vendor ?? '',
            iconUrl: tc.app?.icon_url ?? '',
            category: tc.app?.category ?? '',
            tags: tags ?? [],
            protocol,
            baseUrl: (tc.base_url as string) || mcpServerUrl || '',
            documentationUrl: tc.app?.documentation_url ?? '',
            commonHeaders: recordToHeaders(tc.common_headers),
        },
        auth: {
            authType: wizardAuth,
            enabledAuthTypes,
            apiKeyDelivery,
            apiKeyParamName,
            oauth2GrantType,
            callbackUrl: '',
            authorizationUrl,
            refreshTokenUrl,
            accessTokenUrl,
            scopes,
            audience: '',
            oauth2AuthMethod: 'basic_header',
            customHeaders: [],
        },
        methods,
    };
}
